import * as React from 'react';
import Tag from 'antd/lib/tag';
import Popover from 'antd/lib/popover';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import {
    CheckCircleFilled,
    CheckCircleOutlined,
    InfoCircleOutlined,
    ThunderboltFilled,
    WarningFilled,
    ZoomInOutlined,
    ZoomOutOutlined,
} from '@ant-design/icons';
import Button from 'antd/lib/button';
import {
    AmazonAppStoreData,
    AppleAppStoreDefaultSettings,
    AppPlatform,
    AppStoreAvailableBuild,
    AppStoreReviewSubmissionItemState,
    AppStoreReviewSubmissionItemType,
    AppStoreSelectedBuildReason,
    AppStoreState,
    AppStoreType,
    CIBuildInfo,
    GoogleAppStoreSettings,
    GooglePlayStoreData,
    HuaweiAppGallerySettings,
    ReleaseStepSubmissionData,
    AppleAppStoreData,
    HuaweiAppStoreData,
    ReviewSubmissionItem,
    ReviewAttachment,
    ReviewAttachmentUploadStatus,
    SamsungAppStoreData,
    ReleaseStepType,
    AutomationAutoStagedRollout,
} from '@/types';
import { antGrey, runwayColors, titleStyle } from '@/components/styles/globalStyles';
import {
    ArchetypeSpecificStepRunSummaryPanelProps,
    StepRunSummaryContainer,
    StepRunSummaryDetailItem,
    StepRunSummaryDetailSeparator,
} from '.';
import { StepRun } from '@/types/stepRun';
import {
    statusColorForReleaseStepStatus,
    titleForState,
} from '@/components/StepContentSubmission/StepContentSubmission';
import {
    appleReleaseTypeForReleaseMethod,
    getRolloutDaysToPercentageFromAutomation,
    isAppSubmitted,
    isGooglePlatform,
    oneOfSelectedAppStoreBuildMatchesCIBuild,
    releaseSettingsNeedPhasedReleaseWarning,
} from '@/helpers';
import { labelForInAppUpdatePriority } from '@/helpers/appSettingsHelpers';
import AppStoreBuildComponent from '@/components/appStoreBuilds/AppStoreBuildComponent';
import {
    convertAndroidStagedRolloutPercentage,
    displayConsoleNameForAppStoreType,
    displayStringForRolloutPercentage,
    displayVersionReleaseType,
    displayWarningMessageForSelectedAppStoreBuilds,
    reviewItemNameForType,
} from '@/helpers/displayHelpers';
import { Nullable } from '@/typescriptHelpers';
import { Divider } from 'antd/lib';
import { iconForFileType } from '@/helpers/fileDisplayHelpers';
import { RootState } from '@/reducers/reducers';
import { selectAutomationById } from '@/selectors/selectors';
import { useSelector } from 'react-redux';

const MAX_VISIBLE_BUILDS = 1;

const getTooltipTextForBuildSelectionReason = (
    buildNumber: string,
    selectedBuildReason: Nullable<AppStoreSelectedBuildReason>,
    autoSelectedFromTrack: Nullable<string>,
    platform: AppPlatform,
    appStoreType: AppStoreType,
    isSubmitted: boolean,
): Nullable<string> => {
    if (!selectedBuildReason || selectedBuildReason === AppStoreSelectedBuildReason.Rollback) {
        return null;
    }

    const consoleName = displayConsoleNameForAppStoreType(appStoreType);

    switch (selectedBuildReason) {
        case AppStoreSelectedBuildReason.AutoSelection: {
            const endingText =
                isGooglePlatform(platform) && autoSelectedFromTrack ?
                    ` from the configured track '${autoSelectedFromTrack}'`
                :   '';
            return isSubmitted ?
                    `Runway automatically selected build (#${buildNumber})${endingText} for submission.`
                :   `Runway automatically selected the latest build (#${buildNumber})${endingText}.`;
        }
        case AppStoreSelectedBuildReason.ManualRunwaySelection:
            return isSubmitted ?
                    `The submitted ${consoleName} build (#${buildNumber}) was manually selected in Runway.`
                :   `The selected build (#${buildNumber}) was manually changed in Runway.`;
        case AppStoreSelectedBuildReason.ManualStoreSelection:
            return isSubmitted ?
                    `The submitted ${consoleName} build (#${buildNumber}) was manually selected in ${consoleName}.`
                :   `The selected ${consoleName} build (#${buildNumber}) was manually changed in ${consoleName}.`;
        case AppStoreSelectedBuildReason.ManualRCSelection:
            return isSubmitted ?
                    `The submitted ${consoleName} build's (#${buildNumber}) associated CI build was manually selected in the CI step.`
                :   `The selected ${consoleName} build's (#${buildNumber}) associated CI build was manually selected in the CI step.`;
        default:
            return null;
    }
};

const getBuildSelectionWarnings = (
    selectedBuilds: AppStoreAvailableBuild[],
    activeRCBuild: Nullable<CIBuildInfo>,
    prodBuild: Nullable<CIBuildInfo>,
): string[] => {
    const warnings: string[] = [];

    if (selectedBuilds.length === 0) {
        return warnings;
    }

    if (activeRCBuild?.associatedAppStoreBuild) {
        const matchesActiveRC = oneOfSelectedAppStoreBuildMatchesCIBuild(selectedBuilds, activeRCBuild);
        if (!matchesActiveRC) {
            warnings.push(displayWarningMessageForSelectedAppStoreBuilds(selectedBuilds, activeRCBuild, false));
        }
    }

    if (prodBuild?.associatedAppStoreBuild) {
        const matchesProdBuild = oneOfSelectedAppStoreBuildMatchesCIBuild(selectedBuilds, prodBuild);
        if (!matchesProdBuild) {
            warnings.length = 0;
            warnings.push(displayWarningMessageForSelectedAppStoreBuilds(selectedBuilds, prodBuild, true));
        } else {
            warnings.length = 0;
        }
    }

    return warnings;
};

interface BuildSelectionStatusIconProps {
    selectedBuilds: AppStoreAvailableBuild[];
    activeRCBuild: Nullable<CIBuildInfo>;
    prodBuild: Nullable<CIBuildInfo>;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    isSubmitted: boolean;
}

const BuildSelectionStatusIcon: React.FC<BuildSelectionStatusIconProps> = ({
    selectedBuilds,
    activeRCBuild,
    prodBuild,
    platform,
    appStoreType,
    isSubmitted,
}) => {
    if (!selectedBuilds || selectedBuilds.length === 0) {
        return null;
    }

    const warnings = getBuildSelectionWarnings(selectedBuilds, activeRCBuild, prodBuild);
    const hasWarnings = warnings.length > 0;

    const firstBuild = selectedBuilds[0];
    const isAutoSelection = firstBuild.selectedBuildReason === AppStoreSelectedBuildReason.AutoSelection;

    const selectionReasonTooltip = getTooltipTextForBuildSelectionReason(
        firstBuild.appStoreBuild.buildNumber,
        firstBuild.selectedBuildReason,
        firstBuild.autoSelectedFromTrack,
        platform,
        appStoreType,
        isSubmitted,
    );

    // Priority 1: Show warning icon if there are warnings
    if (hasWarnings) {
        const tooltipContent = (
            <Space direction="vertical" size={4}>
                {warnings.map((warning, idx) => (
                    <span key={idx}>{warning}</span>
                ))}
            </Space>
        );

        return (
            <Tooltip title={tooltipContent}>
                <WarningFilled style={{ color: runwayColors.statusYellow, fontSize: 14, cursor: 'pointer' }} />
            </Tooltip>
        );
    }

    // Priority 2: Show lightning bolt if auto-selected
    if (isAutoSelection) {
        return (
            <Tooltip title={selectionReasonTooltip}>
                <ThunderboltFilled style={{ color: runwayColors.info, fontSize: 14, cursor: 'pointer' }} />
            </Tooltip>
        );
    }

    // Priority 3: Show green check for everything else
    return (
        <Tooltip title={selectionReasonTooltip ?? 'Build selected'}>
            <CheckCircleOutlined style={{ color: runwayColors.statusGreen, fontSize: 14, cursor: 'pointer' }} />
        </Tooltip>
    );
};

interface SelectedBuildsDisplayProps {
    builds: AppStoreAvailableBuild[];
    platform: AppPlatform;
    appStoreType: AppStoreType;
    isSubmitted: boolean;
    activeRCBuild: Nullable<CIBuildInfo>;
    prodBuild: Nullable<CIBuildInfo>;
}

const SelectedBuildsDisplay: React.FC<SelectedBuildsDisplayProps> = ({
    builds,
    platform,
    appStoreType,
    isSubmitted,
    activeRCBuild,
    prodBuild,
}) => {
    if (!builds || builds.length === 0) {
        return null;
    }

    const visibleBuilds = builds.slice(0, MAX_VISIBLE_BUILDS);
    const remainingBuilds = builds.slice(MAX_VISIBLE_BUILDS);
    const remainingCount = remainingBuilds.length;

    return (
        <>
            {visibleBuilds.map((build) => (
                <AppStoreBuildComponent
                    key={build.appStoreBuild.id}
                    {...build}
                    styleOverrides={{
                        backgroundColor: antGrey.grey4,
                    }}
                />
            ))}
            {remainingCount > 0 && (
                <Popover
                    content={
                        <Space direction="vertical" size={8}>
                            {remainingBuilds.map((build) => (
                                <AppStoreBuildComponent
                                    key={build.appStoreBuild.id}
                                    {...build}
                                    styleOverrides={{
                                        backgroundColor: antGrey.grey4,
                                    }}
                                />
                            ))}
                        </Space>
                    }
                    trigger="hover"
                    placement="bottom"
                >
                    <Tag
                        style={{
                            ...titleStyle,
                            fontSize: 10,
                            lineHeight: '22px',
                            margin: 0,
                            color: antGrey.grey7,
                            background: antGrey.grey4,
                            border: `1px solid ${antGrey.grey5}`,
                            cursor: 'pointer',
                        }}
                    >
                        +{remainingCount}
                    </Tag>
                </Popover>
            )}
            <BuildSelectionStatusIcon
                selectedBuilds={builds}
                activeRCBuild={activeRCBuild}
                prodBuild={prodBuild}
                platform={platform}
                appStoreType={appStoreType}
                isSubmitted={isSubmitted}
            />
        </>
    );
};

interface SubmissionItemsCountProps {
    reviewSubmissionItems: Nullable<ReviewSubmissionItem[]>;
}

const getSubmissionItemCountsByType = (
    items: ReviewSubmissionItem[],
): Map<AppStoreReviewSubmissionItemType, number> => {
    const counts = new Map<AppStoreReviewSubmissionItemType, number>();

    for (const item of items) {
        const currentCount = counts.get(item.type) ?? 0;
        counts.set(item.type, currentCount + 1);
    }

    return counts;
};

const SubmissionItemsCount: React.FC<SubmissionItemsCountProps> = ({ reviewSubmissionItems }) => {
    const filteredItems = React.useMemo(
        () => reviewSubmissionItems?.filter((item) => item.state !== AppStoreReviewSubmissionItemState.Removed) ?? [],
        [reviewSubmissionItems],
    );

    if (filteredItems.length === 0) {
        return null;
    }

    const countsByType = getSubmissionItemCountsByType(filteredItems);

    const tooltipContent = (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
            {Array.from(countsByType.entries()).map(([type, count]) => (
                <li key={type}>
                    <Text style={{ color: 'white' }}>
                        {count} {reviewItemNameForType(type).toLowerCase()}
                        {count > 1 ? 's' : ''}
                    </Text>
                </li>
            ))}
        </ul>
    );

    return (
        <Space align="center" size={8}>
            <Text style={{ color: antGrey.grey7, fontSize: 12, fontWeight: 600 }}>{filteredItems.length} items</Text>
            <Tooltip title={tooltipContent}>
                <InfoCircleOutlined style={{ color: runwayColors.info, fontSize: 14, cursor: 'pointer' }} />
            </Tooltip>
        </Space>
    );
};

interface SubmissionStatusTagProps {
    stepRun: StepRun;
    isSubmitted: boolean;
    appStoreType: AppStoreType;
    appStoreState: Nullable<AppStoreState>;
    isReleaseFromScraper: boolean;
    hasSelectedBuild: boolean;
}

const SubmissionStatusTag: React.FC<SubmissionStatusTagProps> = ({
    stepRun,
    isSubmitted,
    appStoreType,
    appStoreState,
    isReleaseFromScraper,
    hasSelectedBuild,
}) => {
    const color = statusColorForReleaseStepStatus(stepRun.status, isReleaseFromScraper);
    const statusText = titleForState(
        isSubmitted,
        appStoreType,
        stepRun.status,
        appStoreState,
        isReleaseFromScraper,
        hasSelectedBuild,
    );

    return (
        <Tag
            style={{
                ...titleStyle,
                fontSize: 10,
                lineHeight: '22px',
                margin: 0,
                color: color,
                background: runwayColors.white,
                border: `1px solid ${color}`,
            }}
        >
            <div
                style={{
                    height: '100%',
                    width: '100%',
                    textAlign: 'center',
                    position: 'relative',
                    top: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    justifyItems: 'center',
                    alignItems: 'stretch',
                    gap: 8,
                }}
            >
                {statusText}
            </div>
        </Tag>
    );
};

interface AppReviewInformationDetailItemProps {
    reviewNotes: Nullable<string>;
    attachments: Nullable<ReviewAttachment[]>;
    contactFirstName: Nullable<string>;
    contactLastName: Nullable<string>;
    contactEmail: Nullable<string>;
    contactPhone: Nullable<string>;
    demoAccountRequired: Nullable<boolean>;
}

const AppReviewInformationDetailItem: React.FC<AppReviewInformationDetailItemProps> = ({
    reviewNotes,
    attachments,
    contactFirstName,
    contactLastName,
    contactEmail,
    contactPhone,
    demoAccountRequired,
}) => {
    const [isExpanded, setIsExpanded] = React.useState<boolean>(false);

    const getCollapsedBody = () => {
        return (
            <Space split={<Divider type="vertical" />} align="center" wrap>
                {reviewNotes ?
                    <Text style={{ color: antGrey.grey7, maxWidth: 600 }} ellipsis={{ tooltip: reviewNotes }}>
                        {reviewNotes}
                    </Text>
                :   <Text style={{ color: antGrey.grey7, fontStyle: 'italic' }}>No notes</Text>}
                {attachments?.map((attachment) => (
                    <Space key={attachment.fileName} align="center" size={4} direction="horizontal" wrap>
                        {iconForFileType(attachment.fileName)}
                        <Text style={{ color: antGrey.grey7 }}>{attachment.fileName}</Text>
                    </Space>
                ))}
            </Space>
        );
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '12px 20px',
                width: '100%',
                position: 'relative',
            }}
        >
            <div style={{ position: 'absolute', right: 12, top: 8, zIndex: 2 }}>
                <Button
                    onClick={() => setIsExpanded(!isExpanded)}
                    style={{ width: 28 }}
                    icon={
                        isExpanded ?
                            <ZoomOutOutlined style={{ color: antGrey.grey6 }} />
                        :   <ZoomInOutlined style={{ color: antGrey.grey6 }} />
                    }
                    type="link"
                />
            </div>

            <Text
                style={{
                    ...titleStyle,
                    color: antGrey.grey7,
                    fontSize: 10,
                }}
            >
                APP REVIEW INFORMATION
            </Text>

            {getCollapsedBody()}

            <div
                style={{
                    borderColor: antGrey.grey4,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    padding: '12px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                    borderRadius: 8,
                    backgroundColor: runwayColors.white,
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                    width: 'calc(100% + 2px)',
                    left: -1,
                    top: -1,
                    zIndex: 3,
                    position: 'absolute',
                    transition: 'all 0.2s ease-in-out',
                    ...(isExpanded ? { opacity: 1 } : { opacity: 0, pointerEvents: 'none' as const }),
                }}
            >
                <div style={{ position: 'absolute', right: 12, top: 8, zIndex: 2 }}>
                    <Button
                        onClick={() => setIsExpanded(false)}
                        style={{ width: 28 }}
                        icon={<ZoomOutOutlined style={{ color: antGrey.grey6 }} />}
                        type="link"
                    />
                </div>

                <Text
                    style={{
                        ...titleStyle,
                        color: antGrey.grey7,
                        fontSize: 10,
                    }}
                >
                    APP REVIEW INFORMATION
                </Text>

                <div style={{ width: '100%', height: 1, backgroundColor: antGrey.grey4 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Text
                        style={{
                            ...titleStyle,
                            color: antGrey.grey7,
                            fontSize: 10,
                        }}
                    >
                        REVIEW NOTES
                    </Text>
                    <Text style={{ color: antGrey.grey8 }}>
                        {reviewNotes || <span style={{ fontStyle: 'italic', color: antGrey.grey6 }}>No notes</span>}
                    </Text>
                </div>

                {attachments && attachments.length > 0 && (
                    <>
                        <div style={{ width: '100%', height: 1, backgroundColor: antGrey.grey4 }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Text
                                style={{
                                    ...titleStyle,
                                    color: antGrey.grey7,
                                    fontSize: 10,
                                }}
                            >
                                ATTACHMENTS
                            </Text>
                            <Space direction="vertical" size={4}>
                                {attachments.map((attachment) => (
                                    <Space key={attachment.fileName} align="center" size={8}>
                                        {iconForFileType(attachment.fileName)}
                                        <Text style={{ color: antGrey.grey8 }}>{attachment.fileName}</Text>
                                    </Space>
                                ))}
                            </Space>
                        </div>
                    </>
                )}

                <div style={{ width: '100%', height: 1, backgroundColor: antGrey.grey4 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Text
                        style={{
                            ...titleStyle,
                            color: antGrey.grey7,
                            fontSize: 10,
                        }}
                    >
                        CONTACT
                    </Text>
                    {contactFirstName || contactLastName || contactEmail || contactPhone ?
                        <Space direction="vertical" size={0}>
                            {(contactFirstName || contactLastName) && (
                                <Text style={{ color: antGrey.grey8 }}>
                                    {[contactFirstName, contactLastName].filter(Boolean).join(' ')}
                                </Text>
                            )}
                            {contactEmail && <Text style={{ color: antGrey.grey8 }}>{contactEmail}</Text>}
                            {contactPhone && <Text style={{ color: antGrey.grey8 }}>{contactPhone}</Text>}
                        </Space>
                    :   <Text style={{ fontStyle: 'italic', color: antGrey.grey6 }}>No contact info</Text>}
                </div>

                <div style={{ width: '100%', height: 1, backgroundColor: antGrey.grey4 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Text
                        style={{
                            ...titleStyle,
                            color: antGrey.grey7,
                            fontSize: 10,
                        }}
                    >
                        SIGN IN REQUIRED
                    </Text>
                    <Text style={{ color: antGrey.grey8 }}>
                        {demoAccountRequired != null ?
                            demoAccountRequired ?
                                'Yes'
                            :   'No'
                        :   <span style={{ fontStyle: 'italic', color: antGrey.grey6 }}>Not set</span>}
                    </Text>
                </div>
            </div>
        </div>
    );
};

interface AppleAppStoreSubmissionDetailsProps {
    appStoreData: Nullable<AppleAppStoreData>;
    appStoreReleaseSettings: Nullable<AppleAppStoreDefaultSettings>;
    platform: AppPlatform;
    isRollback: boolean;
    appDefaultSettings: Nullable<AppleAppStoreDefaultSettings>;
    reviewAttachments: Nullable<ReviewAttachment[]>;
}

const AppleAppStoreSubmissionDetails: React.FC<AppleAppStoreSubmissionDetailsProps> = ({
    appStoreData,
    appStoreReleaseSettings,
    platform,
    isRollback,
    appDefaultSettings,
    reviewAttachments,
}) => {
    const isPhasedRelease = appStoreData?.phasedRelease ? true : (appStoreReleaseSettings?.phasedRelease ?? false);
    const releaseType =
        appStoreData?.versionReviewSubmissionItem?.releaseType ??
        (appStoreReleaseSettings?.releaseMethod ?
            appleReleaseTypeForReleaseMethod(appStoreReleaseSettings.releaseMethod)
        :   undefined);
    const earliestReleaseDate = appStoreData?.versionReviewSubmissionItem?.earliestReleaseDate ?? undefined;

    const phasedReleaseText =
        isPhasedRelease ? 'On (Release will be phased over 7 days)' : 'Off (Release update to all users immediately)';

    const needsPhasedReleaseWarning = releaseSettingsNeedPhasedReleaseWarning(
        platform,
        isRollback,
        appDefaultSettings,
        appStoreReleaseSettings,
    );

    const versionReleaseText = displayVersionReleaseType(releaseType, earliestReleaseDate);

    const usesNonExemptEncryption = appStoreReleaseSettings?.exportComplianceInfoUsesEncryption ?? false;
    const encryptionText = `Uses non-exempt encryption: ${usesNonExemptEncryption ? 'Yes' : 'No'}`;
    const encryptionTooltip =
        'To bypass setting up export compliance in App Store Connect, you can specify your use of encryption directly in the information property list (Info.plist) in your Xcode project. If you need to provide documentation, Apple will provide you with a key value to add to the Info.plist.';

    const reviewDetail = appStoreData?.reviewDetail;
    const reviewNotes = reviewDetail?.notes;
    const completedAttachments = reviewAttachments?.filter(
        (attachment) => attachment.status === ReviewAttachmentUploadStatus.Complete,
    );
    const hasReviewInfo =
        reviewNotes ||
        (completedAttachments && completedAttachments.length > 0) ||
        reviewDetail?.contactFirstName ||
        reviewDetail?.contactLastName ||
        reviewDetail?.contactEmail ||
        reviewDetail?.contactPhone ||
        reviewDetail?.demoAccountRequired != null;

    return (
        <>
            <StepRunSummaryDetailItem header="VERSION RELEASE" body={versionReleaseText} />
            <StepRunSummaryDetailSeparator />
            <StepRunSummaryDetailItem
                header="PHASED RELEASE"
                body={
                    <Space align="center" size={8}>
                        <Text style={{ color: antGrey.grey8 }}>{phasedReleaseText}</Text>
                        {needsPhasedReleaseWarning && (
                            <Tooltip title="Phased release is off for this release because it's a rollback.">
                                <WarningFilled
                                    style={{ color: runwayColors.statusYellow, fontSize: 14, cursor: 'pointer' }}
                                />
                            </Tooltip>
                        )}
                    </Space>
                }
            />
            <StepRunSummaryDetailSeparator />
            <StepRunSummaryDetailItem
                header="EXPORT COMPLIANCE"
                body={
                    <Space align="center" size={8}>
                        <Text style={{ color: antGrey.grey8 }}>{encryptionText}</Text>
                        <Tooltip title={encryptionTooltip}>
                            <InfoCircleOutlined style={{ color: runwayColors.info, fontSize: 14, cursor: 'pointer' }} />
                        </Tooltip>
                    </Space>
                }
            />
            {hasReviewInfo && (
                <>
                    <div style={{ width: '100%', height: 1, backgroundColor: antGrey.grey4 }} />
                    <AppReviewInformationDetailItem
                        reviewNotes={reviewNotes}
                        attachments={completedAttachments}
                        contactFirstName={reviewDetail?.contactFirstName}
                        contactLastName={reviewDetail?.contactLastName}
                        contactEmail={reviewDetail?.contactEmail}
                        contactPhone={reviewDetail?.contactPhone}
                        demoAccountRequired={reviewDetail?.demoAccountRequired}
                    />
                </>
            )}
        </>
    );
};

interface GooglePlayStoreSubmissionDetailsProps {
    googleStoreData: Nullable<GooglePlayStoreData>;
    releaseSettings: Nullable<GoogleAppStoreSettings>;
    appDefaultSettings: Nullable<GoogleAppStoreSettings>;
    platform: AppPlatform;
    isRollback: boolean;
    customStagedRolloutEnabled: boolean;
    stagedRolloutSequence: Nullable<number[]>;
}

const GooglePlayStoreSubmissionDetails: React.FC<GooglePlayStoreSubmissionDetailsProps> = ({
    releaseSettings,
    appDefaultSettings,
    platform,
    isRollback,
    customStagedRolloutEnabled,
    stagedRolloutSequence,
}) => {
    const stagedRolloutEnabled = !!releaseSettings?.stagedRelease;
    const stagedRolloutEnabledText =
        releaseSettings ?
            releaseSettings.stagedRelease ?
                'Yes'
            :   'No'
        :   'Not set';

    const stagedRolloutPercent =
        releaseSettings?.initialStagedRolloutPercentage ?
            displayStringForRolloutPercentage(
                convertAndroidStagedRolloutPercentage(releaseSettings.initialStagedRolloutPercentage),
            )
        :   null;

    const customStagedRolloutActive = customStagedRolloutEnabled && !isRollback;

    const showStagedRolloutPercent = stagedRolloutEnabled && !customStagedRolloutActive;

    const customStagedRolloutAutomationEnabledButStagedRolloutSettingDisabled =
        customStagedRolloutActive && !stagedRolloutEnabled;

    const needsSettingsWarning = customStagedRolloutAutomationEnabledButStagedRolloutSettingDisabled;
    const settingsWarningText =
        "Runway is unable to perform your configured custom staged rollout automation because staged rollout is not enabled. Change this default release method to staged rollout 'Yes' to allow Runway to perform your custom staged rollout.";

    const needsSettingsConfirmation = stagedRolloutEnabled && customStagedRolloutActive;
    const confirmationText =
        "Keep your staged rollout set to 'Yes' to allow Runway to proceed with your custom staged rollout automation.";

    const needsPhasedReleaseWarning = releaseSettingsNeedPhasedReleaseWarning(
        platform,
        isRollback,
        appDefaultSettings,
        releaseSettings,
    );

    const autoStagedRolloutSequence: string[] = [];
    if (stagedRolloutSequence) {
        stagedRolloutSequence.forEach((percent, i) => {
            autoStagedRolloutSequence.push(`Day ${i + 1}: ${percent}%`);
        });
    }

    const showCustomStagedRollout = stagedRolloutEnabled;

    const getStagedRolloutEnabledBody = () => {
        const hasWarningOrConfirmation = needsSettingsWarning || needsSettingsConfirmation || needsPhasedReleaseWarning;

        if (!hasWarningOrConfirmation) {
            return <Text style={{ color: antGrey.grey8 }}>{stagedRolloutEnabledText}</Text>;
        }

        const tooltipMessages: { icon: 'warning' | 'check'; message: string }[] = [];

        if (needsSettingsWarning) {
            tooltipMessages.push({ icon: 'warning', message: settingsWarningText });
        }
        if (needsSettingsConfirmation) {
            tooltipMessages.push({ icon: 'check', message: confirmationText });
        }
        if (needsPhasedReleaseWarning) {
            tooltipMessages.push({
                icon: 'warning',
                message: "Staged rollout has been disabled for this release because it's a rollback.",
            });
        }

        const tooltipContent = (
            <Space direction="vertical" size={4}>
                {tooltipMessages.map((msg, idx) => (
                    <Space key={idx} align="start">
                        {msg.icon === 'warning' ?
                            <WarningFilled style={{ color: runwayColors.statusYellow }} />
                        :   <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />}
                        <Text style={{ color: 'white' }}>{msg.message}</Text>
                    </Space>
                ))}
            </Space>
        );

        const hasWarning = needsSettingsWarning || needsPhasedReleaseWarning;

        return (
            <Space align="center" size={8}>
                <Text style={{ color: antGrey.grey8 }}>{stagedRolloutEnabledText}</Text>
                <Tooltip title={tooltipContent}>
                    {hasWarning ?
                        <WarningFilled style={{ color: runwayColors.statusYellow, fontSize: 14, cursor: 'pointer' }} />
                    :   <CheckCircleFilled
                            style={{ color: runwayColors.statusGreen, fontSize: 14, cursor: 'pointer' }}
                        />
                    }
                </Tooltip>
            </Space>
        );
    };

    const getCustomStagedRolloutBody = () => {
        if (!customStagedRolloutActive) {
            return <Text style={{ color: antGrey.grey8 }}>No</Text>;
        }

        if (stagedRolloutSequence && stagedRolloutSequence.length > 0) {
            const tooltipContent = (
                <Space direction="vertical">
                    <Space direction="vertical" size={0}>
                        {autoStagedRolloutSequence.map((day, i) => (
                            <Text style={{ color: 'white' }} key={i}>
                                {day}
                            </Text>
                        ))}
                    </Space>
                    <Text style={{ color: 'white' }}>
                        You can configure your custom staged rollout from automation settings.
                    </Text>
                </Space>
            );

            return (
                <Space align="center" size={8}>
                    <Text style={{ color: antGrey.grey8 }}>Yes</Text>
                    <Tooltip title={tooltipContent}>
                        <InfoCircleOutlined style={{ color: antGrey.grey6, fontSize: 14, cursor: 'pointer' }} />
                    </Tooltip>
                </Space>
            );
        }

        // Custom staged rollout is enabled but we don't have the sequence details
        return <Text style={{ color: antGrey.grey8 }}>Yes</Text>;
    };

    return (
        <>
            <StepRunSummaryDetailItem header="STAGED ROLLOUT ENABLED" body={getStagedRolloutEnabledBody()} />
            {showCustomStagedRollout && (
                <>
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem header="CUSTOM STAGED ROLLOUT" body={getCustomStagedRolloutBody()} />
                </>
            )}
            {showStagedRolloutPercent && (
                <>
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem
                        header="INITIAL ROLLOUT PERCENTAGE"
                        body={stagedRolloutPercent ?? 'Not set'}
                    />
                </>
            )}
            <StepRunSummaryDetailSeparator />
            <StepRunSummaryDetailItem
                header="IN-APP UPDATE PRIORITY"
                body={labelForInAppUpdatePriority(releaseSettings?.inAppUpdatePriority)}
            />
        </>
    );
};

interface HuaweiAppStoreSubmissionDetailsProps {
    huaweiData: Nullable<HuaweiAppStoreData>;
    releaseSettings: Nullable<HuaweiAppGallerySettings>;
    appDefaultSettings: Nullable<HuaweiAppGallerySettings>;
    customPhasedReleaseEnabled: boolean;
    phasedReleaseSequence: Nullable<number[]>;
}

const HuaweiAppStoreSubmissionDetails: React.FC<HuaweiAppStoreSubmissionDetailsProps> = ({
    releaseSettings,
    customPhasedReleaseEnabled,
    phasedReleaseSequence,
}) => {
    const phasedReleaseEnabled = !!releaseSettings?.stagedRelease;
    const phasedReleaseEnabledText =
        releaseSettings ?
            releaseSettings.stagedRelease ?
                'Yes'
            :   'No'
        :   'Not set';

    const phasedReleasePercent =
        releaseSettings?.initialStagedRolloutPercentage ?
            displayStringForRolloutPercentage(
                convertAndroidStagedRolloutPercentage(releaseSettings.initialStagedRolloutPercentage),
            )
        :   null;

    const customPhasedReleaseActive = customPhasedReleaseEnabled;
    const showPhasedReleasePercent = phasedReleaseEnabled && !customPhasedReleaseActive;

    const customPhasedReleaseAutomationEnabledButPhasedReleaseSettingDisabled =
        customPhasedReleaseActive && !phasedReleaseEnabled;

    const needsSettingsWarning = customPhasedReleaseAutomationEnabledButPhasedReleaseSettingDisabled;
    const settingsWarningText =
        "Runway is unable to perform your configured custom phased release automation because phased release is not enabled. Change this default release method to phased release 'Yes' to allow Runway to perform your custom phased release.";

    const needsSettingsConfirmation = phasedReleaseEnabled && customPhasedReleaseActive;
    const confirmationText =
        "Keep your phased release set to 'Yes' to allow Runway to proceed with your custom phased release automation.";

    const autoPhasedReleaseSequence: string[] = [];
    if (phasedReleaseSequence) {
        phasedReleaseSequence.forEach((percent, i) => {
            autoPhasedReleaseSequence.push(`Day ${i + 1}: ${percent}%`);
        });
    }

    const showCustomPhasedRelease = phasedReleaseEnabled;

    const getPhasedReleaseEnabledBody = () => {
        const hasWarningOrConfirmation = needsSettingsWarning || needsSettingsConfirmation;

        if (!hasWarningOrConfirmation) {
            return <Text style={{ color: antGrey.grey8 }}>{phasedReleaseEnabledText}</Text>;
        }

        const tooltipMessages: { icon: 'warning' | 'check'; message: string }[] = [];

        if (needsSettingsWarning) {
            tooltipMessages.push({ icon: 'warning', message: settingsWarningText });
        }
        if (needsSettingsConfirmation) {
            tooltipMessages.push({ icon: 'check', message: confirmationText });
        }

        const tooltipContent = (
            <Space direction="vertical" size={4}>
                {tooltipMessages.map((msg, idx) => (
                    <Space key={idx} align="start">
                        {msg.icon === 'warning' ?
                            <WarningFilled style={{ color: runwayColors.statusYellow }} />
                        :   <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />}
                        <Text style={{ color: 'white' }}>{msg.message}</Text>
                    </Space>
                ))}
            </Space>
        );

        const hasWarning = needsSettingsWarning;

        return (
            <Space align="center" size={8}>
                <Text style={{ color: antGrey.grey8 }}>{phasedReleaseEnabledText}</Text>
                <Tooltip title={tooltipContent}>
                    {hasWarning ?
                        <WarningFilled style={{ color: runwayColors.statusYellow, fontSize: 14, cursor: 'pointer' }} />
                    :   <CheckCircleFilled
                            style={{ color: runwayColors.statusGreen, fontSize: 14, cursor: 'pointer' }}
                        />
                    }
                </Tooltip>
            </Space>
        );
    };

    const getCustomPhasedReleaseBody = () => {
        if (!customPhasedReleaseActive) {
            return <Text style={{ color: antGrey.grey8 }}>No</Text>;
        }

        if (phasedReleaseSequence && phasedReleaseSequence.length > 0) {
            const tooltipContent = (
                <Space direction="vertical">
                    <Space direction="vertical" size={0}>
                        {autoPhasedReleaseSequence.map((day, i) => (
                            <Text style={{ color: 'white' }} key={i}>
                                {day}
                            </Text>
                        ))}
                    </Space>
                    <Text style={{ color: 'white' }}>
                        You can configure your custom phased release from automation settings.
                    </Text>
                </Space>
            );

            return (
                <Space align="center" size={8}>
                    <Text style={{ color: antGrey.grey8 }}>Yes</Text>
                    <Tooltip title={tooltipContent}>
                        <InfoCircleOutlined style={{ color: antGrey.grey6, fontSize: 14, cursor: 'pointer' }} />
                    </Tooltip>
                </Space>
            );
        }

        return <Text style={{ color: antGrey.grey8 }}>Yes</Text>;
    };

    return (
        <>
            <StepRunSummaryDetailItem header="PHASED RELEASE ENABLED" body={getPhasedReleaseEnabledBody()} />
            {showCustomPhasedRelease && (
                <>
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem header="CUSTOM PHASED RELEASE" body={getCustomPhasedReleaseBody()} />
                </>
            )}
            {showPhasedReleasePercent && (
                <>
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem
                        header="INITIAL PHASED RELEASE PERCENTAGE"
                        body={phasedReleasePercent ?? 'Not set'}
                    />
                </>
            )}
        </>
    );
};

interface SubmissionDetailsFactoryParams {
    appStoreType: AppStoreType;
    stepData: ReleaseStepSubmissionData;
    isRollback: boolean;
    stagedRolloutSequence: Nullable<number[]>;
    reviewAttachments: Nullable<ReviewAttachment[]>;
}

const getSubmissionDetailsByAppStoreType = ({
    appStoreType,
    stepData,
    isRollback,
    stagedRolloutSequence,
    reviewAttachments,
}: SubmissionDetailsFactoryParams): React.ReactNode => {
    switch (appStoreType) {
        case AppStoreType.Apple:
            return (
                <AppleAppStoreSubmissionDetails
                    appStoreData={stepData.appStoreData as AppleAppStoreData}
                    appStoreReleaseSettings={stepData.appStoreReleaseSettings as AppleAppStoreDefaultSettings}
                    platform={stepData.platform}
                    appDefaultSettings={stepData.appStoreReleaseSettings as AppleAppStoreDefaultSettings}
                    isRollback={isRollback}
                    reviewAttachments={reviewAttachments}
                />
            );
        case AppStoreType.Google:
            return (
                <GooglePlayStoreSubmissionDetails
                    googleStoreData={stepData.appStoreData as GooglePlayStoreData}
                    releaseSettings={stepData.appStoreReleaseSettings as GoogleAppStoreSettings}
                    appDefaultSettings={stepData.appStoreReleaseSettings as GoogleAppStoreSettings}
                    platform={stepData.platform}
                    isRollback={isRollback}
                    customStagedRolloutEnabled={!!stepData.autoStagedRolloutAutomationId}
                    stagedRolloutSequence={stagedRolloutSequence}
                />
            );
        case AppStoreType.Huawei:
            return (
                <HuaweiAppStoreSubmissionDetails
                    huaweiData={stepData.appStoreData as HuaweiAppStoreData}
                    releaseSettings={stepData.appStoreReleaseSettings as HuaweiAppGallerySettings}
                    appDefaultSettings={stepData.appStoreReleaseSettings as HuaweiAppGallerySettings}
                    customPhasedReleaseEnabled={!!stepData.autoStagedRolloutAutomationId}
                    phasedReleaseSequence={stagedRolloutSequence}
                />
            );
        default:
            return null;
    }
};

export const SubmissionStepRunSummaryPanel: React.FC<ArchetypeSpecificStepRunSummaryPanelProps> = ({
    stepRun,
    appId,
}) => {
    const stepData: ReleaseStepSubmissionData = { ...stepRun.data.summaryData, ...stepRun.data.complementaryData };

    const appStoreData = stepData.appStoreData as AppleAppStoreData;
    const googleStoreData = stepData.appStoreData as GooglePlayStoreData;
    const amazonData = stepData.appStoreData as AmazonAppStoreData;
    const huaweiData = stepData.appStoreData as HuaweiAppStoreData;
    const samsungData = stepData.appStoreData as SamsungAppStoreData;

    const isSubmitted = isAppSubmitted(
        stepData.appStoreType,
        appStoreData?.versionReviewSubmissionItem?.state,
        appStoreData?.relevantReviewSubmission?.state,
        googleStoreData?.state,
        amazonData?.state,
        huaweiData?.state,
        samsungData?.state,
    );

    const appleAppStoreState = appStoreData?.versionReviewSubmissionItem?.state;
    const googleAppStoreState = googleStoreData?.state;
    const amazonAppStoreState = amazonData?.state;
    const huaweiAppStoreState = huaweiData?.state;
    const samsungAppStoreState = samsungData?.state;

    let appStoreState: Nullable<AppStoreState>;
    if (appleAppStoreState) {
        appStoreState = appleAppStoreState;
    } else if (googleAppStoreState) {
        appStoreState = googleAppStoreState;
    } else if (amazonAppStoreState) {
        appStoreState = amazonAppStoreState;
    } else if (huaweiAppStoreState) {
        appStoreState = huaweiAppStoreState;
    } else if (samsungAppStoreState) {
        appStoreState = samsungAppStoreState;
    }

    const hasSelectedBuild = stepData.appStoreSelectedBuilds?.length > 0;
    const isRollback = stepRun.type === ReleaseStepType.RollbackBuild;

    const autoStagedRolloutAutomationId = stepData.autoStagedRolloutAutomationId;

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, appId, autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const stagedRolloutSequence = getRolloutDaysToPercentageFromAutomation(autoStagedRolloutAutomation);

    return (
        <div>
            <StepRunSummaryContainer
                topBar={
                    <>
                        <SubmissionStatusTag
                            stepRun={stepRun}
                            isSubmitted={isSubmitted}
                            appStoreType={stepData.appStoreType}
                            appStoreState={appStoreState}
                            isReleaseFromScraper={false}
                            hasSelectedBuild={hasSelectedBuild}
                        />
                        <SelectedBuildsDisplay
                            builds={stepData.appStoreSelectedBuilds ?? []}
                            platform={stepData.platform}
                            appStoreType={stepData.appStoreType}
                            isSubmitted={isSubmitted}
                            activeRCBuild={stepData.latestSuccessfulBuild}
                            prodBuild={stepData.prodBuild}
                        />
                        {appStoreData?.relevantReviewSubmission?.reviewSubmissionItems?.length > 1 && (
                            <>
                                <Divider type="vertical" style={{ height: 22 }} />
                                <SubmissionItemsCount
                                    reviewSubmissionItems={
                                        appStoreData?.relevantReviewSubmission?.reviewSubmissionItems
                                    }
                                />
                            </>
                        )}
                    </>
                }
            >
                {getSubmissionDetailsByAppStoreType({
                    appStoreType: stepData.appStoreType,
                    stepData,
                    isRollback,
                    stagedRolloutSequence,
                    reviewAttachments: appStoreData?.reviewDetail?.reviewAttachments,
                })}
            </StepRunSummaryContainer>
        </div>
    );
};
